import { registerPlugin } from '@capacitor/core';
import CallablePromise from './utils/CallablePromise';
import Encoding from './utils/Encoding';
const CapacitorThermalPrinterImplementation = registerPlugin('CapacitorThermalPrinter');
const wrappedMethodsArgNames = {
    //#region Text Formatting
    bold: ['enabled'],
    underline: ['enabled'],
    doubleWidth: ['enabled'],
    doubleHeight: ['enabled'],
    inverse: ['enabled'],
    //#endregion
    //#region Image Formatting
    dpi: ['dpi'],
    limitWidth: ['width'],
    //#endregion
    //#region Data Code Formatting
    barcodeWidth: ['width'],
    barcodeHeight: ['height'],
    barcodeTextPlacement: ['placement'],
    //#endregion
    //#region Hybrid Formatting
    align: ['alignment'],
    charSpacing: ['charSpacing'],
    lineSpacing: ['lineSpacing'],
    font: ['font'],
    clearFormatting: [],
    //#endregion
    //#region Content
    text: ['text'],
    image: ['image'],
    qr: ['data'],
    barcode: ['type', 'data'],
    raw: ['data'],
    selfTest: [],
    //#endregion
    //#region Content Actions
    beep: [],
    openDrawer: [],
    cutPaper: ['half'],
    feedCutPaper: ['half'],
    //#endregion
    //#region Printing Actions
    begin: [],
    write: [],
    //#endregion
};
const wrappedMethodsMiddleware = {
    async image(data) {
        return { image: await Encoding.toBase64(data) };
    },
    async raw(data) {
        return { data: await Encoding.toBase64(data) };
    },
};
function mapArgs(key, args) {
    if (key in wrappedMethodsMiddleware) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return wrappedMethodsMiddleware[key](...args);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const argNames = wrappedMethodsArgNames[key];
    return Object.fromEntries(argNames.map((name, index) => [name, structuredClone(args[index])]));
}
const connectionQueues = new Map();
const sessions = new Map();
let activeConnectionId = null;
function ensureQueue(connectionId) {
    let queue = connectionQueues.get(connectionId);
    if (!queue) {
        queue = [];
        connectionQueues.set(connectionId, queue);
    }
    return queue;
}
function createSession(connectionIdResolver) {
    const session = {};
    const sessionProxy = session;
    for (const key in wrappedMethodsArgNames) {
        session[key] = (...args) => {
            const connectionId = connectionIdResolver();
            const queue = ensureQueue(connectionId);
            const trailingLock = queue.pop();
            const lock = new CallablePromise();
            queue.push(lock);
            const promise = Promise.resolve(trailingLock)
                .then(async () => {
                const mappedArgs = await Promise.resolve(mapArgs(key, args));
                const payload = Object.assign({ connectionId }, mappedArgs);
                return CapacitorThermalPrinterImplementation[key](payload);
            })
                .finally(() => {
                lock.resolve();
            });
            if (key === 'write') {
                return promise;
            }
            return sessionProxy;
        };
    }
    return sessionProxy;
}
function ensureSession(connectionId) {
    let session = sessions.get(connectionId);
    if (!session) {
        session = createSession(() => connectionId);
        sessions.set(connectionId, session);
    }
    ensureQueue(connectionId);
    return session;
}
function clearSession(connectionId) {
    var _a;
    sessions.delete(connectionId);
    const queue = connectionQueues.get(connectionId);
    if (queue) {
        for (const lock of queue) {
            lock.resolve();
        }
    }
    connectionQueues.delete(connectionId);
    if (activeConnectionId === connectionId) {
        activeConnectionId = sessions.size ? (_a = Array.from(sessions.keys())[0]) !== null && _a !== void 0 ? _a : null : null;
    }
}
function resolveConnectionId(preferred) {
    if (preferred) {
        return preferred;
    }
    if (activeConnectionId) {
        return activeConnectionId;
    }
    if (sessions.size === 1) {
        return Array.from(sessions.keys())[0];
    }
    throw new Error('No active printer connection. Set an active connection or pass a connectionId.');
}
const defaultSession = createSession(() => resolveConnectionId());
const CapacitorThermalPrinter = Object.assign(defaultSession, {
    async connect(options) {
        const result = (await CapacitorThermalPrinterImplementation.connect(options));
        if (result) {
            ensureSession(result.connectionId);
            if (!activeConnectionId) {
                activeConnectionId = result.connectionId;
            }
        }
        return result;
    },
    async disconnect(options) {
        var _a;
        const connectionId = resolveConnectionId((_a = options === null || options === void 0 ? void 0 : options.connectionId) !== null && _a !== void 0 ? _a : null);
        await CapacitorThermalPrinterImplementation.disconnect({ connectionId });
        clearSession(connectionId);
    },
    async isConnected(options) {
        const payload = {};
        if (options === null || options === void 0 ? void 0 : options.connectionId) {
            payload.connectionId = options.connectionId;
        }
        const { state } = await CapacitorThermalPrinterImplementation.isConnected(payload);
        return Boolean(state);
    },
    async listConnections() {
        const { connections } = (await CapacitorThermalPrinterImplementation.listConnections());
        for (const connection of connections) {
            ensureSession(connection.connectionId);
        }
        if (!activeConnectionId && connections.length > 0) {
            activeConnectionId = connections[0].connectionId;
        }
        return { connections };
    },
    useConnection(connectionId) {
        const session = ensureSession(connectionId);
        return session;
    },
    setActiveConnection(connectionId) {
        if (connectionId === null) {
            activeConnectionId = null;
            return;
        }
        if (!sessions.has(connectionId)) {
            throw new Error(`Unknown connectionId: ${connectionId}`);
        }
        activeConnectionId = connectionId;
    },
    getActiveConnection() {
        return activeConnectionId;
    },
    startScan: (...args) => CapacitorThermalPrinterImplementation.startScan(...args),
    stopScan: (...args) => CapacitorThermalPrinterImplementation.stopScan(...args),
    addListener: CapacitorThermalPrinterImplementation.addListener.bind(CapacitorThermalPrinterImplementation),
});
// Keep internal bookkeeping in sync with native events.
CapacitorThermalPrinterImplementation.addListener('connected', (device) => {
    ensureSession(device.connectionId);
    if (!activeConnectionId) {
        activeConnectionId = device.connectionId;
    }
});
CapacitorThermalPrinterImplementation.addListener('disconnected', (data) => {
    if (!(data === null || data === void 0 ? void 0 : data.connectionId)) {
        return;
    }
    clearSession(data.connectionId);
});
export * from './definitions';
export { CapacitorThermalPrinter };
//# sourceMappingURL=index.js.map